import React from "react";
import { Paper, Box, Typography, Grid, Skeleton } from "@mui/material";
import GameCard from "../components/GameCard";
import {
  SelectedGame,
  GetAllPicksForWeekResponse,
  PickedGame,
} from "../types/game";
import { PollsResponse } from "../types/poll";
import { RecordsResponse } from "../types/records";
import { getTeamPollRank } from "../utils/poll";
import { getTeamRecord } from "../utils/records";
import { sortGamesByStartStatus, isGameStarted } from "../utils/game";
import useUsers from "../../../hooks/useUsers/useUsers";
import { useAvatarCache } from "../../../contexts/AvatarCacheContext";

interface GameResultsProps {
  selectedGames: SelectedGame[];
  gameOfTheWeek: number | null;
  userPicks: GetAllPicksForWeekResponse | null;
  pollsData: PollsResponse | null;
  recordsData: RecordsResponse | null;
  isMobile: boolean;
  weekReady: boolean;
  selectedYear: number;
  selectedWeek: number;
  userEmail?: string;
  rules?: any;
  winningTeams: Record<string, number>;
  loading?: boolean;
  getUserPick: (gameId: number, email: string) => PickedGame | undefined;
  getGameResult: (
    game: any,
    pick: PickedGame | undefined
  ) => "correct" | "incorrect" | "push" | "pending";
}

const GameResults: React.FC<GameResultsProps> = ({
  selectedGames,
  gameOfTheWeek,
  userPicks,
  pollsData,
  recordsData,
  isMobile,
  weekReady,
  selectedYear,
  selectedWeek,
  userEmail,
  rules,
  winningTeams,
  loading = false,
  getUserPick,
  getGameResult,
}) => {
  const { getCachedUsers } = useUsers();
  const { getCachedAvatar } = useAvatarCache();

  if (loading || selectedGames.length === 0) {
    return <GameResultsSkeleton isMobile={isMobile} />;
  }

  if (!weekReady) {
    return (
      <Paper elevation={2}>
        <Box
          sx={{
            background: "linear-gradient(135deg, #1976d2, #42a5f5)",
            color: "white",
            padding: isMobile ? 1.5 : 2,
          }}
        >
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            sx={{ fontWeight: 600 }}
          >
            Game Results
          </Typography>
        </Box>
        <Box sx={{ padding: isMobile ? 1 : 2 }}>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              color: "#666",
              padding: isMobile ? 2 : 4,
            }}
          >
            This week is still in draft. Selected games will be visible after
            publish.
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (selectedGames.length === 0) {
    return (
      <Paper elevation={2}>
        <Box
          sx={{
            background: "linear-gradient(135deg, #1976d2, #42a5f5)",
            color: "white",
            padding: isMobile ? 1.5 : 2,
          }}
        >
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            sx={{ fontWeight: 600 }}
          >
            Game Results
          </Typography>
        </Box>
        <Box sx={{ padding: isMobile ? 1 : 2 }}>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              color: "#666",
              padding: isMobile ? 2 : 4,
            }}
          >
            No games selected for this week.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={2}>
      <Box
        sx={{
          background: "linear-gradient(135deg, #1976d2, #42a5f5)",
          color: "white",
          padding: isMobile ? 1.5 : 2,
        }}
      >
        <Typography
          variant={isMobile ? "subtitle1" : "h6"}
          sx={{ fontWeight: 600 }}
        >
          Game Results
        </Typography>
      </Box>
      <Box sx={{ padding: isMobile ? 1 : 2 }}>
        <Grid container spacing={isMobile ? 1.5 : 3}>
          {sortGamesByStartStatus(
            selectedGames,
            (game) => game.game.time,
            (game) => game.game.completed ?? false
          ).map((selectedGame, index) => {
            const game = selectedGame.game;
            const homeTeam = selectedGame.homeTeam;
            const awayTeam = selectedGame.awayTeam;

            // Get all user picks for this game from visible picks API (independent of weekScores)
            const visiblePickEmails = Object.keys(userPicks?.picks || {});
            const gamePicks: Array<{
              email: string;
              pick: PickedGame | undefined;
              result: "correct" | "incorrect" | "push" | "pending";
            }> = visiblePickEmails.map((email) => {
              const userPick = getUserPick(game.id, email);
              return {
                email,
                pick: userPick,
                result: getGameResult(game, userPick),
              };
            });

            // Get current user's pick for this game
            const currentUserPick = userEmail
              ? getUserPick(game.id, userEmail)
              : undefined;
            const currentUserResult = getGameResult(game, currentUserPick);

            // Group users by their picks
            const homeTeamPickers = gamePicks
              .filter((pick) => pick.pick?.pick === homeTeam.id)
              .map((pick) => pick.email);
            const awayTeamPickers = gamePicks
              .filter((pick) => pick.pick?.pick === awayTeam.id)
              .map((pick) => pick.email);

            // Get cached user data for avatar stacks
            const homeTeamUsers = getCachedUsers(homeTeamPickers);
            const awayTeamUsers = getCachedUsers(awayTeamPickers);

            // Determine display mode based on current user's pick
            let displayMode:
              | "normal"
              | "correct"
              | "incorrect"
              | "push"
              | "disabled"
              | "in_progress"
              | "no_pick" = "normal";

            if (userEmail && !(currentUserPick as any)) {
              // User exists but hasn't made a pick - show as no pick
              displayMode = "no_pick";
            } else if (game.completed && userEmail) {
              // Map currentUserResult to displayMode, handling "pending" case
              if (currentUserResult === "pending") {
                displayMode = "normal";
              } else {
                displayMode = currentUserResult;
              }
            } else if (isGameStarted(game.time) && !game.completed) {
              // Game has started but not finished - show as in progress
              displayMode = "in_progress";
            }

            const isGameOfTheWeek = game.id === gameOfTheWeek;

            // Add winning team data to the game object
            const gameWithWinningTeam = {
              ...game,
              winningTeam: winningTeams[String(game.id)],
            };

            // Compute current user's avatar info
            const currentUserEmail = userEmail;
            const currentUserAvatar = (() => {
              if (!currentUserEmail || !currentUserPick) return undefined;
              const user = getCachedUsers([currentUserEmail])[0];
              const avatarUrl = getCachedAvatar(currentUserEmail);
              const initials = (user?.displayName || currentUserEmail)
                .split(" ")
                .map((s) => s[0])
                .join("")
                .slice(0, 2)
                .toUpperCase();
              const side =
                currentUserPick.pick === awayTeam.id
                  ? "away"
                  : currentUserPick.pick === homeTeam.id
                  ? "home"
                  : undefined;
              if (!side) return undefined;
              return { initials, url: avatarUrl, side } as const;
            })();

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Box>
                  <GameCard
                    game={gameWithWinningTeam}
                    metadata={selectedGame.metadata}
                    homeTeam={homeTeam}
                    awayTeam={awayTeam}
                    currentUserAvatar={currentUserAvatar}
                    displayMode={displayMode}
                    // Let GameCard decide; it shows scores if present
                    homeScore={game.homePoints}
                    awayScore={game.awayPoints}
                    isGameOfTheWeek={isGameOfTheWeek}
                    isLive={isGameStarted(game.time) && !game.completed}
                    homeTeamPickers={homeTeamPickers}
                    awayTeamPickers={awayTeamPickers}
                    homeTeamUsers={homeTeamUsers}
                    awayTeamUsers={awayTeamUsers}
                    showLines={Boolean(
                      rules?.flavorLinesRules?.showGameLines !== false
                    )}
                    showScoringLine={Boolean(
                      rules?.flavorLinesRules?.customGameLinesEnabled
                    )}
                    showLiveLine={
                      !(
                        rules?.flavorLinesRules?.customGameLinesEnabled &&
                        rules?.flavorLinesRules?.hideOfficialLinesWhenCustomEnabled
                      )
                    }
                    showCustomLine={Boolean(
                      rules?.flavorLinesRules?.customGameLinesEnabled
                    )}
                    year={selectedYear}
                    week={selectedWeek}
                    homeTeamPollRank={getTeamPollRank(
                      homeTeam.school,
                      pollsData
                    )}
                    awayTeamPollRank={getTeamPollRank(
                      awayTeam.school,
                      pollsData
                    )}
                    homeTeamRecord={getTeamRecord(homeTeam.id, recordsData)}
                    awayTeamRecord={getTeamRecord(awayTeam.id, recordsData)}
                    currentUserEmail={userEmail}
                  />
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Paper>
  );
};

// Skeleton component for loading state
export const GameResultsSkeleton: React.FC<{ isMobile?: boolean }> = ({
  isMobile = false,
}) => {
  const mobilePadding = isMobile ? 1.5 : 2;
  const mobileSpacing = isMobile ? 1.5 : 3;

  return (
    <Paper elevation={2}>
      <Box
        sx={{
          background: "linear-gradient(135deg, #1976d2, #42a5f5)",
          color: "white",
          padding: mobilePadding,
        }}
      >
        <Skeleton
          variant="text"
          width="40%"
          height={isMobile ? 20 : 24}
          sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
        />
      </Box>
      <Box sx={{ p: mobilePadding }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: mobileSpacing,
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <Paper key={i} elevation={1} sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Skeleton variant="text" width="40%" height={20} />
                <Skeleton variant="text" width="30%" height={20} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Skeleton variant="text" width="35%" height={16} />
                <Skeleton variant="text" width="35%" height={16} />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <Skeleton variant="text" width="20%" height={16} />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="circular" width={32} height={32} />
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default GameResults;
